import { useState } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { Modal, ModalFooter, Btn, Input } from '../ui';
import { FolderPicker } from './FolderPicker';
import { forceOfficeWebEdit } from '../../utils/docViewer';

// NewOfficeFileModal — start a blank Word / Excel / PowerPoint file on a deal.
//
// The server creates it in the deal's SharePoint folder first and records it as
// a document already linked there, so it opens in Office Online at once. Edits
// made there come back to Harvus by the SharePoint sync (every few minutes) as
// new versions, and the file is re-read so its summary and search follow.
//
// The tab is opened BEFORE the request is awaited, or the popup blocker
// treats it as unprompted; it is pointed at the file once the server answers.

export const NEW_FILE_KINDS = [
  { kind: 'word',       label: 'Word',       ext: 'docx' },
  { kind: 'excel',      label: 'Excel',      ext: 'xlsx' },
  { kind: 'powerpoint', label: 'PowerPoint', ext: 'pptx' },
];

export function NewOfficeFileModal({ dealId, dealDetailId, initialFolder = '', folderTree, liveFolders,
                                     foldersLoading, foldersError, sharePointAvailable, onClose, onCreated }) {
  const [kind, setKind] = useState('word');
  const [name, setName] = useState('');
  const [folder, setFolder] = useState(initialFolder || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ext = NEW_FILE_KINDS.find(k => k.kind === kind)?.ext;

  const submit = async () => {
    if (!name.trim()) { setErr('Give the file a name.'); return; }
    setBusy(true); setErr(null);
    const tab = window.open('', '_blank');
    try {
      const res = await api.post('documents/new', {
        kind, name: name.trim(), folderPath: folder || undefined,
        ...(dealDetailId ? { dealDetailId } : { dealId }),
      }, { timeout: 60000 });
      if (res?.webUrl && tab) tab.location.href = forceOfficeWebEdit(res.webUrl);
      else if (tab) tab.close();
      onCreated?.(res);
      onClose?.();
    } catch (e) {
      if (tab) tab.close();
      setErr(e?.message || 'The file could not be created.');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="New file" width={520} onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }} role="radiogroup" aria-label="File type">
        {NEW_FILE_KINDS.map(k => (
          <button key={k.kind} type="button" role="radio" aria-checked={kind === k.kind}
            onClick={() => setKind(k.kind)} disabled={busy}
            style={{ flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 14, fontWeight: 600,
              border: `1px solid ${kind === k.kind ? B.navy : B.border}`,
              background: kind === k.kind ? B.navy : B.white, color: kind === k.kind ? '#fff' : B.navy }}>
            {k.label}
          </button>
        ))}
      </div>
      <Input label={`Name (.${ext} is added)`} value={name} onChange={v => { setName(v); setErr(null); }}
        placeholder="e.g. Q3 investor update" />
      <FolderPicker
        label="Folder (optional)"
        hint="Left empty, the file stays at the deal's top level — it is not moved for you while you work in it."
        tree={folderTree}
        folders={liveFolders}
        loading={foldersLoading}
        error={foldersError}
        selected={folder}
        onSelect={setFolder}
        sharePointAvailable={sharePointAvailable}
      />
      <div style={{ fontSize: 12, color: B.textMd, marginTop: 4 }}>
        Opens in Office Online. Your edits come back to Harvus within a few minutes as a new version.
      </div>
      {err && <div style={{ fontSize: 12, color: B.red, marginTop: 10 }}>{err}</div>}
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={submit} loading={busy} disabled={busy || !name.trim()}>Create and open</Btn>
      </ModalFooter>
    </Modal>
  );
}

export default NewOfficeFileModal;
